import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DesignV2Scope, SegmentedControl } from "../../../design-v2";
import { StackHeader } from "../../../components/product";
import { CURRENT_ANALYSIS } from "../api/insightsApi";
import { VOLUME_BASIS } from "../domain/insightsPresentation";
import {
  InsightsSection,
  NoActiveCycleState,
  NoHistoryState,
  SectionError,
  SectionLoading,
  TimeframeSelector,
} from "../components/InsightsChrome";
import {
  BASIS_OPTIONS,
  MAX_CHART_MUSCLES,
  MuscleSheet,
  MuscleSlots,
  MuscleVolumeChart,
  WeekPager,
  useMuscleVolumeModel,
} from "../components/MuscleVolumeParts";
import { insightsPath, useMuscleVolume, useTimeframeParam } from "./useInsightsData";

const BASIS_TITLES = {
  [VOLUME_BASIS.WEIGHTED]: "Weighted sets / week",
  [VOLUME_BASIS.DIRECT]: "Direct sets / week",
  [VOLUME_BASIS.INDIRECT]: "Indirect sets / week",
};

function MuscleVolumeBody({ data, basis }) {
  const model = useMuscleVolumeModel(data);
  const [weekIndex, setWeekIndex] = useState(model.weekIndex);
  const [muscles, setMuscles] = useState(model.defaultMuscles);
  const [picking, setPicking] = useState(false);
  const week = Math.min(Math.max(weekIndex, 0), model.columns.length - 1);

  const toggleMuscle = (key) => setMuscles((current) => {
    if (current.includes(key)) return current.filter((entry) => entry !== key);
    return current.length >= MAX_CHART_MUSCLES ? current : [...current, key];
  });
  const removeMuscle = (key) => setMuscles((current) => current.filter((entry) => entry !== key));

  return (
    <>
      <InsightsSection title={BASIS_TITLES[basis]} headingLevel="h2" testId="muscle-volume-panel">
        <MuscleVolumeChart
          columns={model.columns}
          muscles={muscles}
          basis={basis}
          highlightIndex={week}
          title="Actual vs planned"
          hasVolume={model.hasVolume}
        />
        <WeekPager columns={model.columns} weekIndex={week} onChange={setWeekIndex} />
        {model.groups.length ? (
          <MuscleSlots
            columns={model.columns}
            muscles={muscles}
            basis={basis}
            weekIndex={week}
            onRemove={removeMuscle}
            onEdit={() => setPicking(true)}
          />
        ) : null}
      </InsightsSection>
      <MuscleSheet
        open={picking}
        onClose={() => setPicking(false)}
        groups={model.groups}
        columns={model.columns}
        basis={basis}
        weekIndex={week}
        selected={muscles}
        onToggle={toggleMuscle}
      />
    </>
  );
}

export default function MuscleVolumeScreen() {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useTimeframeParam();
  const state = useMuscleVolume(timeframe);
  const [basis, setBasis] = useState(VOLUME_BASIS.WEIGHTED);

  let body;
  let showBasis = false;
  if (state.status === "loading") body = <SectionLoading label="Loading muscle volume" />;
  else if (state.status === "error") body = <SectionError what="muscle volume" onRetry={state.retry} />;
  else if (state.data?.available === false) body = <NoActiveCycleState onBrowseHistory={() => changeTimeframe("3M")} />;
  else if (timeframe !== CURRENT_ANALYSIS && !(state.data?.weeks || []).length) body = <NoHistoryState timeframe={timeframe} />;
  // Keyed by timeframe: a new period starts from its own default week and muscles.
  else {
    body = <MuscleVolumeBody key={timeframe} data={state.data} basis={basis} />;
    showBasis = true;
  }

  // A new period starts from the default set count again.
  const changeTimeframe = (next) => {
    setBasis(VOLUME_BASIS.WEIGHTED);
    setTimeframe(next);
  };

  return (
    <DesignV2Scope className="lz-insights lz-insights--detail" data-testid="insights-muscle-volume">
      <StackHeader
        title="Muscle Volume"
        backLabel="Back to Insights"
        onBack={() => navigate(insightsPath("/progress", timeframe))}
      />
      <main className="lz-insights__content">
        <TimeframeSelector value={timeframe} onChange={changeTimeframe}>
          {showBasis ? (
            <SegmentedControl
              label="Set count"
              className="lz-insights-basis"
              value={basis}
              onChange={setBasis}
              options={BASIS_OPTIONS}
            />
          ) : null}
        </TimeframeSelector>
        {body}
      </main>
    </DesignV2Scope>
  );
}
